import React, { useState, useCallback, useEffect } from 'react';
import { AlertApi } from '../../services/api/AlertApi';

const METRIC_TYPES = [
  { value: 'GPT_COST', label: 'GPT Cost ($)', description: 'Total GPT API spend in the evaluation window' },
  { value: 'LATENCY', label: 'Avg Latency (ms)', description: 'Average GPT response latency' },
  { value: 'ENGAGEMENT_DROP', label: 'Engagement Drop (%)', description: 'Percentage drop in user engagement vs prior window' },
  { value: 'CHURN_RISK', label: 'Churn Risk (%)', description: 'Percentage of users inactive >14 days' },
];

const CONDITION_OPS = ['>', '>=', '<', '<=', '=='];

const RULE_TEMPLATES = [
  { name: 'High GPT Cost', metricType: 'GPT_COST', conditionOp: '>', threshold: 50, windowMinutes: 60, cooldownMinutes: 360, description: 'Alert when hourly GPT cost exceeds $50' },
  { name: 'Latency Spike', metricType: 'LATENCY', conditionOp: '>', threshold: 5000, windowMinutes: 30, cooldownMinutes: 120, description: 'Alert when avg latency exceeds 5 seconds' },
  { name: 'Engagement Drop', metricType: 'ENGAGEMENT_DROP', conditionOp: '>', threshold: 30, windowMinutes: 1440, cooldownMinutes: 1440, description: 'Alert when daily engagement drops by 30%+' },
  { name: 'Churn Risk', metricType: 'CHURN_RISK', conditionOp: '>', threshold: 25, windowMinutes: 1440, cooldownMinutes: 4320, description: 'Alert when >25% of users are at risk of churning' },
];

const cardStyle = {
  background: 'var(--card-bg, #fff)',
  borderRadius: '12px',
  border: '1px solid var(--border-light, #dee2e6)',
  padding: '1.25rem',
  boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,.05))',
};

/**
 * AlertSettings — Admin page for configuring alert rules and viewing alert history.
 * Route: /settings/alerts (admin only)
 */
export default function AlertSettings() {
  const [activeTab, setActiveTab] = useState('rules');

  return (
    <div className="settings-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>🔔 Alert Settings</h2>
          <p style={{ margin: '.25rem 0 0', fontSize: '.85rem', color: 'var(--text-muted)' }}>
            Configure automated alerts for cost thresholds, latency spikes, and engagement anomalies.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-light)', marginBottom: '1.5rem' }}>
        {[
          { key: 'rules', label: '📋 Alert Rules' },
          { key: 'create', label: '➕ Create Rule' },
          { key: 'history', label: '📜 Alert History' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '.75rem 1.25rem',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab.key ? '#007bff' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.key ? 600 : 400,
              cursor: 'pointer',
              fontSize: '.85rem',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'rules' && <RulesTab />}
      {activeTab === 'create' && <CreateRuleTab onCreated={() => setActiveTab('rules')} />}
      {activeTab === 'history' && <HistoryTab />}
    </div>
  );
}

// ─── Rules Tab ──────────────────────────────────────────────────────

function RulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AlertApi.getRules();
      setRules(data);
    } catch (err) {
      console.error('Failed to load alert rules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleDeactivate = async (id) => {
    try {
      await AlertApi.deactivateRule(id);
      loadRules();
    } catch (err) {
      console.error('Failed to deactivate rule:', err);
    }
  };

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      const result = await AlertApi.evaluate();
      alert(`Evaluation complete: ${result.alertsFired} alert(s) fired`);
    } catch (err) {
      console.error('Failed to evaluate:', err);
    } finally {
      setEvaluating(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading rules…</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{rules.length} rule(s) configured</span>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="secondary" onClick={loadRules}>↻ Refresh</button>
          <button onClick={handleEvaluate} disabled={evaluating}>
            {evaluating ? '⏳ Evaluating…' : '▶ Run Evaluation Now'}
          </button>
        </div>
      </div>

      {rules.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🔕</div>
          <p style={{ color: 'var(--text-muted)' }}>No alert rules configured yet. Create one to start monitoring.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {rules.map(rule => (
            <div key={rule.id} style={{ ...cardStyle, opacity: rule.active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <span style={{ fontSize: '.7rem', padding: '.15rem .4rem', borderRadius: '4px', fontWeight: 600, background: rule.active ? '#28a74520' : '#6c757d20', color: rule.active ? '#28a745' : '#6c757d' }}>
                      {rule.active ? 'ACTIVE' : 'DISABLED'}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '.95rem' }}>{rule.name}</h3>
                  </div>
                  {rule.description && <p style={{ margin: '.25rem 0 0', fontSize: '.8rem', color: 'var(--text-muted)' }}>{rule.description}</p>}
                </div>
                {rule.active && (
                  <button className="secondary" onClick={() => handleDeactivate(rule.id)} style={{ fontSize: '.75rem' }}>
                    Disable
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '.75rem', flexWrap: 'wrap' }}>
                <RuleProp label="Metric" value={rule.metricType?.replace(/_/g, ' ')} />
                <RuleProp label="Condition" value={`${rule.conditionOp} ${rule.threshold}`} />
                <RuleProp label="Window" value={`${rule.windowMinutes} min`} />
                <RuleProp label="Cooldown" value={`${rule.cooldownMinutes} min`} />
                <RuleProp label="Email" value={rule.notifyEmail ? '✅' : '❌'} />
                <RuleProp label="In-App" value={rule.notifyInApp ? '✅' : '❌'} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleProp({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '.85rem' }}>{value}</div>
    </div>
  );
}

// ─── Create Rule Tab ────────────────────────────────────────────────

function CreateRuleTab({ onCreated }) {
  const [form, setForm] = useState({
    name: '', description: '', metricType: 'GPT_COST', conditionOp: '>',
    threshold: '', windowMinutes: 60, cooldownMinutes: 360,
    notifyEmail: true, notifyInApp: true, recipients: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const applyTemplate = (template) => {
    setForm(f => ({
      ...f,
      name: template.name,
      description: template.description,
      metricType: template.metricType,
      threshold: template.threshold,
      windowMinutes: template.windowMinutes,
      cooldownMinutes: template.cooldownMinutes,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await AlertApi.createRule({
        ...form,
        threshold: parseFloat(form.threshold),
        windowMinutes: parseInt(form.windowMinutes),
        cooldownMinutes: parseInt(form.cooldownMinutes),
      });
      onCreated();
    } catch (err) {
      console.error('Failed to create rule:', err);
      alert('Failed to create rule: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMetric = METRIC_TYPES.find(m => m.value === form.metricType);

  return (
    <div>
      {/* Quick Templates */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 .5rem 0', fontSize: '.85rem' }}>Quick Templates</h4>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          {RULE_TEMPLATES.map((t, i) => (
            <button key={i} className="secondary" onClick={() => applyTemplate(t)} style={{ fontSize: '.8rem' }}>
              {t.name}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} style={cardStyle}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '.95rem' }}>Create Alert Rule</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <FormField label="Rule Name" required>
            <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g. High GPT Cost" />
          </FormField>
          <FormField label="Metric Type" required>
            <select name="metricType" value={form.metricType} onChange={handleChange}>
              {METRIC_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </FormField>
          <FormField label="Condition" required>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <select name="conditionOp" value={form.conditionOp} onChange={handleChange} style={{ width: '80px' }}>
                {CONDITION_OPS.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
              <input name="threshold" type="number" step="any" value={form.threshold} onChange={handleChange} required placeholder="Threshold value" style={{ flex: 1 }} />
            </div>
          </FormField>
          <FormField label="Evaluation Window (minutes)">
            <input name="windowMinutes" type="number" value={form.windowMinutes} onChange={handleChange} min={1} />
          </FormField>
          <FormField label="Cooldown (minutes)">
            <input name="cooldownMinutes" type="number" value={form.cooldownMinutes} onChange={handleChange} min={1} />
          </FormField>
          <FormField label="Email Recipients">
            <input name="recipients" value={form.recipients} onChange={handleChange} placeholder="admin@example.com, ops@example.com" />
          </FormField>
          <FormField label="Description">
            <input name="description" value={form.description} onChange={handleChange} placeholder="Optional description" />
          </FormField>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.85rem' }}>
              <input type="checkbox" name="notifyEmail" checked={form.notifyEmail} onChange={handleChange} />
              Email alerts
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.85rem' }}>
              <input type="checkbox" name="notifyInApp" checked={form.notifyInApp} onChange={handleChange} />
              In-app alerts
            </label>
          </div>
        </div>

        {selectedMetric && (
          <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: '.75rem' }}>
            ℹ️ {selectedMetric.description}
          </p>
        )}

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '.5rem' }}>
          <button type="submit" disabled={submitting}>
            {submitting ? '⏳ Creating…' : '✅ Create Rule'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label style={{ fontSize: '.75rem', fontWeight: 600, display: 'block', marginBottom: '.25rem' }}>
        {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── History Tab ────────────────────────────────────────────────────

function HistoryTab() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AlertApi.getHistory(page, 20);
      setAlerts(data.content || []);
      setHasMore(!data.last);
    } catch (err) {
      console.error('Failed to load alert history:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleAck = async (id) => {
    try {
      await AlertApi.acknowledgeAlert(id);
      loadHistory();
    } catch (err) {
      console.error('Failed to acknowledge:', err);
    }
  };

  const handleAckAll = async () => {
    try {
      await AlertApi.acknowledgeAll();
      loadHistory();
    } catch (err) {
      console.error('Failed to acknowledge all:', err);
    }
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading history…</p>;

  const severityColors = {
    CRITICAL: { bg: '#dc354515', border: '#dc354560', text: '#dc3545', icon: '🚨' },
    WARNING: { bg: '#ffc10715', border: '#ffc10760', text: '#e6a700', icon: '⚠️' },
    INFO: { bg: '#17a2b815', border: '#17a2b860', text: '#17a2b8', icon: '💡' },
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{alerts.length} alert(s) on this page</span>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="secondary" onClick={loadHistory}>↻ Refresh</button>
          <button className="secondary" onClick={handleAckAll}>✓ Acknowledge All</button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>✅</div>
          <p style={{ color: 'var(--text-muted)' }}>No alerts have been triggered yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '.75rem' }}>
          {alerts.map(a => {
            const sev = severityColors[a.severity] || severityColors.INFO;
            return (
              <div key={a.id} style={{ ...cardStyle, borderLeft: `4px solid ${sev.border}`, background: a.acknowledged ? 'var(--card-bg)' : sev.bg, opacity: a.acknowledged ? 0.7 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                      <span>{sev.icon}</span>
                      <span style={{ fontSize: '.7rem', fontWeight: 700, color: sev.text, textTransform: 'uppercase' }}>{a.severity}</span>
                      <span style={{ fontSize: '.85rem', fontWeight: 600 }}>{a.ruleName}</span>
                    </div>
                    <p style={{ margin: '.25rem 0 0', fontSize: '.8rem', color: 'var(--text-muted)' }}>{a.message}</p>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '.5rem', fontSize: '.75rem', color: 'var(--text-muted)' }}>
                      <span>Value: {a.metricValue?.toFixed(4)}</span>
                      <span>Threshold: {a.threshold?.toFixed(4)}</span>
                      <span>{a.firedAt ? new Date(a.firedAt).toLocaleString() : ''}</span>
                      {a.notifiedEmail && <span>📧 Emailed</span>}
                    </div>
                  </div>
                  {!a.acknowledged && (
                    <button className="secondary" onClick={() => handleAck(a.id)} style={{ fontSize: '.75rem', whiteSpace: 'nowrap' }}>
                      ✓ Ack
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
        <button className="secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Previous</button>
        <span style={{ fontSize: '.85rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Page {page + 1}</span>
        <button className="secondary" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>
    </div>
  );
}
